import api from "../services/api";
import { APIS } from "../endpoints";
import { hasFileNameFilter } from "../util/fileNameFilter";
import { broadcastRecordChange } from "../hooks/useRecordSync";

// Grid cards only render these fields; broadcast just enough for other tabs
// to tell whether their visible list actually changed.
const gridSnapshot = (data = {}) => ({
  title: data.title,
  sortable_year: data.sortable_year,
  direct_url_of_preview_image: data.direct_url_of_preview_image,
  direct_url_of_cover_image: data.direct_url_of_cover_image,
  isVisible: data.isVisible,
});

/**
 * This function return songs data
 * @returns {Promise<Object>} - Function return Songs data
 */

// export const getArchiveData = async (limit, page) => {
//     const response = await getArchiveAPIData(limit, page);
//     return response;
// };

// Returns true when the filter payload has no user-applied selections.
// Backend treats any non-empty `filters` query param as a filtered query and
// returns an empty result, so we OMIT the param entirely on initial loads
// (default state: empty filters object + default sortBy_v + empty search).
const _isDefaultFilterOptions = (opts) => {
  if (!opts || typeof opts !== "object") return true;
  const filters = opts.filters;
  const hasFilterValues =
    filters &&
    typeof filters === "object" &&
    Object.values(filters).some((v) =>
      Array.isArray(v) ? v.length > 0 : Boolean(v),
    );
  if (hasFilterValues) return false;
  if (opts.search) return false;
  if (hasFileNameFilter(opts)) return false;
  return true;
};

export const getAllLibrariesData = async (
  limit,
  page,
  searchTerm,
  filterOptions = {},
) => {
  const formData = new FormData();
  formData.append("page", page);
  formData.append("limit", limit);

  if (!_isDefaultFilterOptions(filterOptions)) {
    const cleanedOptions = {
      ...filterOptions,
      filters: Object.fromEntries(
        Object.entries(filterOptions.filters || {}).filter(([, v]) =>
          Array.isArray(v) ? v.length > 0 : Boolean(v),
        ),
      ),
    };
    formData.append("filters", JSON.stringify(cleanedOptions));
  }

  if (searchTerm) {
    formData.append("search", searchTerm); // Add search term
  }

  const params = new URLSearchParams(formData);
  const result = await api.get(`${APIS.BASE_URL}${APIS.libraries}`, {
    params: params,
  });

  if (result) {
    return result;
  } else {
    return [];
  }
};

export const getLibrariesData = async (alias, section, type) => {
  // Construct the endpoint URL conditionally based on the presence of `section`
  const endpoint = section
    ? `${APIS.BASE_URL}admin/${type}/${alias}/general/${section}`
    : `${APIS.BASE_URL}admin/${type}/${alias}`;

  try {
    const result = await api.get(endpoint);

    // Return the `data` if the result exists, otherwise return an empty array
    return result?.data?.data || [];
  } catch (error) {
    console.error("Error fetching libraries data:", error);
    return [];
  }
};

export const updateLibrariesData = async (alias, data, deletedValues) => {
  const payload = {
    ...data,
    deletedValues: deletedValues,
  };

  const result = await api.put(
    `${APIS.BASE_URL}admin/libraries/${alias}/general`,
    payload,
  );

  if (result) {
    broadcastRecordChange("libraries", {
      id: alias,
      action: "update",
      snapshot: gridSnapshot(data),
    });
    return result?.data;
  } else {
    return [];
  }
};

/**
 * Update libraries highlight data with keywords
 * @param {string} alias - Library alias
 * @param {Object} data - Highlight data to update (should have keywords array)
 * @returns {Promise<Object>} - API response
 */
export const updateLibrariesHighlightData = async (alias, data, type) => {
  try {
    const result = await api.put(
      `${APIS.BASE_URL}admin/${type}/${alias}/general/highlight?highlight=yes`,
      data,
    );

    if (result) {
      return result?.data;
    } else {
      return [];
    }
  } catch (error) {
    console.error("Error updating libraries highlight data:", error);
    throw error;
  }
};

/**
 * Update highlight data for libraries or archive (common function)
 * @param {string} alias - Item alias
 * @param {string} highlightValue - "yes" or "no"
 * @param {string} type - Type of item: "libraries" or "archive"
 * @returns {Promise<Object>} - API response
 */
export const updateHighlightData = async (
  alias,
  highlightValue,
  type,
  masterlistAlias,
) => {
  try {
    // Construct endpoint based on type
    const endpoint = `${APIS.BASE_URL}admin/${type}/${alias}/general/highlight?highlight=${highlightValue}&masterlist_alias=${masterlistAlias}`;

    const result = await api.put(endpoint);

    if (result) {
      return result?.data;
    } else {
      return [];
    }
  } catch (error) {
    console.error(`Error updating ${type} highlight data:`, error);
    throw error;
  }
};

/**
 * Update archive highlight data (for yes/no scenarios)
 * @param {string} alias - Archive alias
 * @param {string} highlightValue - "yes" or "no"
 * @returns {Promise<Object>} - API response
 * @deprecated Use updateHighlightData instead
 */
export const updateArchiveHighlightData = async (alias, highlightValue) => {
  return updateHighlightData(alias, highlightValue, "archive");
};

export const getResearchCentreLibrariesDetailsAPI = async (
  masterlist,
  masterlistType,
  bookAlias,
) => {
  const result = await api.get(
    `${APIS.BASE_URL}${masterlistType}/${masterlist}/research-centre/libraries/${bookAlias}/details`,
  );
  return result?.data;
};

export const getResearchCentreBookbyBookAliasAPI = async (
  masterlist,
  masterlistType,
  bookAlias,
) => {
  const result = await api.get(
    `${APIS.BASE_URL}${masterlistType}/${masterlist}/research-centre/library/${bookAlias}/imageDetails`,
  );
  return result?.data;
};

export const getResearchCentreBookbyBookAlias = async (
  masterlist,
  masterlistType,
  bookAlias,
) => {
  const response = await getResearchCentreBookbyBookAliasAPI(
    masterlist,
    masterlistType,
    bookAlias,
  );
  try {
    return response;
  } catch (e) {
    console.error("Something is wrong", e);
    return { data: [] };
  }
};

export const getArchiveCastDetails = async (masterlist) => {
  const result = await api.get(
    `${APIS.BASE_URL}film/${masterlist}/body-of-work/cast`,
  );
  if (result) {
    return result?.data;
  } else {
    return [];
  }
};

export const introductionByAliasAPI = async (masterlistType, masterlist) => {
  const result = await api.get(
    `${APIS.BASE_URL}${masterlistType}/${masterlist}/${APIS.personality}`,
  );
  if (result) {
    return result?.data?.data;
  } else {
    return [];
  }
};

export const createLibraryRecord = async (data) => {
  const result = await api.post(`${APIS.BASE_URL}admin/libraries`, data);

  if (result) {
    broadcastRecordChange("libraries", { action: "create" });
    return result?.data;
  } else {
    return [];
  }
};

export const getBulkEditConfig = async () => {
  const result = await api.get(
    `${APIS.BASE_URL}${APIS.librariesBulkEditConfig}`,
  );
  return result?.data?.fields || [];
};

export const bulkEditLibraries = async (payload) => {
  const result = await api.patch(
    `${APIS.BASE_URL}${APIS.librariesBulk}`,
    payload,
  );
  const data = result?.data;
  if (data?.success) {
    // "create" is reused here purely because isGridChangeRelevant always treats
    // it as relevant — a bulk edit can touch records other tabs aren't
    // currently rendering, so there's no single id/snapshot to check instead.
    broadcastRecordChange("libraries", { action: "create" });
  }
  return data;
};

export const updateLibraryVisibility = async (alias, isVisible) => {
  try {
    const currentData = await getLibrariesData(alias, "section-1", "libraries");
    const record = Array.isArray(currentData)
      ? currentData[0]
      : currentData?.data?.[0];
    const updatedData = { ...(record || {}), isVisible };
    const result = await api.put(
      `${APIS.BASE_URL}admin/libraries/${alias}/general`,
      updatedData,
    );
    return result?.data;
  } catch (error) {
    console.error("Error updating library visibility:", error);
    throw error;
  }
};
